'use client';

import { AlarmClock, AwardIcon, CheckIcon, X } from 'lucide-react';

import type { ResponseDashTriviaInit } from '@/types/dashboard';

interface TriviaResultStatsProps {
  triviaData?: ResponseDashTriviaInit;
  totalStats?: boolean;
  isWinner: boolean;
  triviaTime: string;
  isMobile?: boolean;
}

export const TriviaResultStats = ({
  triviaData,
  totalStats,
  isWinner,
  triviaTime,
  isMobile = false,
}: TriviaResultStatsProps) => {
  const getStatsData = () => {
    if (totalStats) {
      return {
        leftTitle: 'MEDALLAS GANADAS',
        leftValue: triviaData?.resultadoRanking?.medallasTotales ?? 0,
        leftColor: 'text-gdt-gray-2',
        leftBg: 'bg-gdt-status-yellow-light',
        leftIcon: (
          <AwardIcon
            className="stroke-gdt-status-yellow"
            size={isMobile ? 18 : 22}
          />
        ),
        rightTitle: 'MEJOR TIEMPO',
        rightValue: (
          <span className={!isMobile ? 'mt-5 block' : ''}>
            {triviaTime || '-'}
          </span>
        ),
        rightBg: `bg-gdt-status-orange-light ${!isMobile ? 'mt-5' : ''}`,
        rightIcon: (
          <AlarmClock
            className={`stroke-gdt-status-orange`}
            size={isMobile ? 18 : 22}
          />
        ),
      };
    } else {
      return {
        leftTitle: 'Respuestas',
        leftValue: `${triviaData?.resultadoParticipacion?.cantidadRespuestasCorrectas} de ${triviaData?.resultadoParticipacion?.cantidadPreguntas}`,
        leftColor: isWinner ? 'text-gdt-status-green' : 'text-gdt-status-red',
        leftBg: isWinner
          ? 'bg-gdt-status-green-light'
          : 'bg-gdt-status-red-light',
        leftIcon: isWinner ? (
          <CheckIcon
            className="stroke-gdt-status-green"
            size={isMobile ? 18 : 22}
          />
        ) : (
          <X className="stroke-gdt-status-red" size={isMobile ? 18 : 22} />
        ),
        rightTitle: 'Tiempo',
        rightValue: triviaTime,
        rightBg: isWinner
          ? 'bg-gdt-status-green-light'
          : 'bg-gdt-status-red-light',
        rightIcon: (
          <AlarmClock
            className={
              isWinner ? 'stroke-gdt-status-green' : 'stroke-gdt-status-red'
            }
            size={isMobile ? 18 : 20}
          />
        ),
      };
    }
  };

  const stats = getStatsData();
  const cardPadding = isMobile ? 'px-3 py-4' : 'p-5';
  const iconContainer = isMobile
    ? 'p-2'
    : 'w-8 h-8 flex justify-center items-center';
  const textSize = isMobile ? 'text-lg min-[390px]:text-xl' : 'text-2xl';
  const timeTextSize = isMobile ? 'text-base min-[390px]:text-lg' : 'text-2xl';

  return (
    <div className={`grid grid-cols-2 gap-x-6 ${isMobile ? 'px-5 mt-5' : ''}`}>
      {/* Left Card */}
      <div
        className={`${cardPadding} ${
          isMobile ? 'bg-white' : 'bg-gdt-gray-7'
        } rounded-lg flex flex-col justify-center`}
      >
        <div
          className={`flex flex-col ${
            isMobile ? 'gap-y-2' : 'gap-y-3'
          } items-center md:items-start`}
        >
          <span
            className={`font-bold text-gdt-gray-3 ${
              totalStats ? 'text-lg' : 'text-xl'
            }`}
          >
            {stats.leftTitle}
          </span>

          <div className="flex items-center justify-center md:justify-start gap-x-2 leading-none">
            <span
              className={`${textSize} font-bold ${stats.leftColor} ${
                isMobile ? 'whitespace-nowrap' : ''
              }`}
            >
              {stats.leftValue}
            </span>
            <div
              className={`${iconContainer} rounded-full ${stats.leftBg} flex items-center justify-center translate-y-[1px]`}
            >
              {stats.leftIcon}
            </div>
          </div>

          {!totalStats && (
            <span
              className={`font-semibold ${
                isWinner ? 'text-gdt-status-green' : 'text-gdt-status-red'
              } ${isMobile ? 'whitespace-nowrap' : ''}`}
            >
              {isWinner ? '¡Muy bien! Seguí así' : 'Podés mejorar'}
            </span>
          )}
        </div>
      </div>

      {/* Right Card */}
      <div
        className={`${cardPadding} ${
          isMobile ? 'bg-white py-5' : 'bg-gdt-gray-7'
        } rounded-lg flex flex-col justify-center`}
      >
        <div
          className={`flex flex-col ${
            isMobile ? 'gap-y-2' : 'gap-y-3'
          } items-center md:items-start`}
        >
          <span
            className={`font-bold text-gdt-gray-3 ${
              totalStats ? 'text-lg' : 'text-xl'
            }`}
          >
            {stats.rightTitle}
          </span>

          {/* 👇 Ajuste: ícono alineado perfectamente con el texto */}
          <div className="flex items-center justify-center md:justify-start gap-x-2 leading-none">
            <span className={`${timeTextSize} font-bold text-gdt-gray-2`}>
              {stats.rightValue}
            </span>
            <div
              className={`${iconContainer} rounded-full ${stats.rightBg} flex items-center justify-center translate-y-[2px]`}
            >
              {stats.rightIcon}
            </div>
          </div>

          {!totalStats && (
            <span
              className={`font-semibold ${
                isWinner ? 'text-gdt-status-green' : 'text-gdt-status-red'
              } ${isMobile ? 'whitespace-nowrap' : ''}`}
            >
              {isWinner ? '¡Muy bien! Seguí así' : 'Podés mejorar'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
